import Image from 'next/image';
import { Edit } from 'react-feather';

import { Tags } from '@/components';
import { useAuthToken } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { getPlannerUrlWithDraft } from '@/utils/links';

import styles from './CampaignPlan.module.css';

interface CampaignPlanProps {
  draft: Api.Planner.v1PublicCampaignDraft;
}

interface CampaignStep {
  number: number;
  label: string;
  value: string;
  isDone: boolean;
}

function getCampaignSteps(
  draft: Api.Planner.v1PublicCampaignDraft,
  t: (key: string) => string
): CampaignStep[] {
  const campaignType = draft.user_input?.campaign_type;
  const steps: CampaignStep[] = [];

  if (campaignType === 'influencer') {
    // Step 1: Settings
    const country = draft.user_input?.campaign_country || '';
    steps.push({
      number: 1,
      label: t('plans.stepSettings'),
      value: country || '',
      isDone: !!country
    });

    // Step 2: Objectives
    const objectives = draft.user_input?.campaign_objectives || [];
    const objectiveValue = objectives.length > 0 ? objectives[0] : '';
    steps.push({
      number: 2,
      label: t('plans.stepObjectives'),
      value: objectiveValue || '',
      isDone: objectives.length > 0
    });

    // Step 3: Audit
    const hasMarketAnalysis = !!draft.market_analysis;
    steps.push({
      number: 3,
      label: t('plans.stepAudit'),
      value: hasMarketAnalysis ? t('plans.stepDone') : t('plans.stepTodo'),
      isDone: hasMarketAnalysis
    });

    // Step 4: Audience
    const selectedAudience = draft.audiences?.find((aud) => aud.is_selected);
    const audienceName = selectedAudience?.name || '';
    steps.push({
      number: 4,
      label: t('plans.stepAudience'),
      value: audienceName || t('plans.stepTodo'),
      isDone: !!selectedAudience
    });

    // Step 5: Concepts
    const selectedConcept = draft.concepts?.find((concept) => concept.is_selected);
    const conceptTitle = selectedConcept?.title || '';
    steps.push({
      number: 5,
      label: t('plans.stepConcept'),
      value: conceptTitle || t('plans.stepTodo'),
      isDone: !!selectedConcept
    });

    // Step 6: Mediaplan
    const hasMediaplan = !!draft.mediaplan;
    steps.push({
      number: 6,
      label: t('plans.stepMediaplan'),
      value: hasMediaplan ? t('plans.stepDone') : t('plans.stepTodo'),
      isDone: hasMediaplan
    });
  } else if (campaignType === 'ugc') {
    // Step 1: Settings
    const country = draft.user_input?.campaign_country || '';
    steps.push({
      number: 1,
      label: t('plans.stepSettings'),
      value: country || '',
      isDone: !!country
    });

    // Step 2: ICPs
    const hasICPs = !!draft.icp_and_personas;
    steps.push({
      number: 2,
      label: t('plans.stepICPs'),
      value: hasICPs ? t('plans.stepDone') : t('plans.stepTodo'),
      isDone: hasICPs
    });

    // Step 3: Moodboard
    const hasMoodboard =
      !!draft.user_input?.selected_references && draft.user_input.selected_references.length > 0;
    steps.push({
      number: 3,
      label: t('plans.stepMoodboard'),
      value: hasMoodboard ? t('plans.stepDone') : t('plans.stepTodo'),
      isDone: hasMoodboard
    });

    // Step 4: Creators
    const hasCreators =
      !!draft.user_input?.selected_collections && draft.user_input.selected_collections.length > 0;
    steps.push({
      number: 4,
      label: t('plans.stepCreators'),
      value: hasCreators ? t('plans.stepDone') : t('plans.stepTodo'),
      isDone: hasCreators
    });
  }

  return steps;
}

function calculateProgress(
  draft: Api.Planner.v1PublicCampaignDraft,
  t: (key: string) => string
): number {
  const steps = getCampaignSteps(draft, t);
  if (steps.length === 0) return 0;

  const doneSteps = steps.filter((step) => step.isDone).length;
  return Math.round((doneSteps / steps.length) * 100);
}

function getPromotableTypeLabel(type: string, t: (key: string) => string): string {
  const typeMap: Record<string, string> = {
    SERVICE: t('plans.typeService'),
    BRAND: t('plans.typeBrand'),
    PRODUCT: t('plans.typeProduct'),
    IDEA: t('plans.typeIdea'),
    EVENT: t('plans.typeEvent'),
    OTHER: t('plans.typeOther')
  };
  return typeMap[type] || type;
}

function getLogoUrl(draft: Api.Planner.v1PublicCampaignDraft): string | null {
  if (draft.brand?.logo_url && draft.brand.logo_url !== 'NO_LOGO') {
    return draft.brand.logo_url;
  }
  if (draft.promotable?.pictures && draft.promotable.pictures.length > 0) {
    return draft.promotable.pictures[0].url;
  }
  return null;
}

export function CampaignPlan({ draft }: CampaignPlanProps) {
  const { t } = useTranslation();
  const jwtToken = useAuthToken();
  const steps = getCampaignSteps(draft, t);
  const progress = calculateProgress(draft, t);

  const campaignName =
    draft.user_input?.campaign_name || draft.promotable?.name || 'Untitled Campaign';
  const promotableType = draft.promotable?.type || 'OTHER';
  const description = draft.promotable?.description || '';
  const tags = draft.promotable?.tags || [];
  const logoUrl = getLogoUrl(draft);

  const editPlanUrl = getPlannerUrlWithDraft(draft.draft_id, jwtToken);

  return (
    <section className={styles.campaignPlan}>
      <div className={styles.campaignPlanGrid}>
        <div className={styles.campaignPlanInfo}>
          <div className={styles.campaignPlanInfoHeader}>
            <h2 className={styles.campaignPlanName}>{campaignName}</h2>
            <p className={styles.campaignPlanType}>
              Type: {getPromotableTypeLabel(promotableType, t)}
            </p>
          </div>

          {logoUrl && (
            <div className={styles.campaignPlanLogo}>
              <Image
                src={logoUrl}
                alt={campaignName}
                width={96}
                height={96}
                className={styles.campaignPlanLogoImage}
              />
            </div>
          )}

          {description && <p className={styles.campaignPlanDescription}>{description}</p>}

          {tags.length > 0 && (
            <div className={styles.campaignPlanTags}>
              <Tags
                tags={tags.map((tag) => ({
                  value: tag,
                  theme: 'DEFAULT'
                }))}
              />
            </div>
          )}
        </div>

        <div className={styles.campaignPlanStatus}>
          <div className={styles.campaignPlanStatusHeader}>
            <div className={styles.campaignPlanStatusLabel}>
              <span className={styles.campaignPlanStatusText}>{t('plans.status')}</span>
              <span className={styles.campaignPlanStatusValue}>{t('plans.statusDraft')}</span>
            </div>
            <a
              href={editPlanUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.campaignPlanEditButton}
            >
              <Edit size={16} />
              <span>{t('plans.editPlan')}</span>
            </a>
          </div>

          <div className={styles.campaignPlanSteps}>
            {steps.map((step) => (
              <div key={step.number} className={styles.campaignPlanStep}>
                <div className={styles.campaignPlanStepNumber} data-is-done={step.isDone}>
                  {step.number}
                </div>
                <p className={styles.campaignPlanStepLabel}>{step.label}</p>
                <p
                  className={`${styles.campaignPlanStepValue} ${
                    !step.isDone ? styles.campaignPlanStepValueTodo : ''
                  }`}
                >
                  {step.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.campaignPlanProgress}>
        <p className={styles.campaignPlanProgressLabel}>
          {t('plans.progress')}: {progress}%
        </p>
        <div className={styles.campaignPlanProgressBar}>
          <div className={styles.campaignPlanProgressBarFill} style={{ width: `${progress}%` }} />
        </div>
      </div>
    </section>
  );
}
